import { useMemo, useState } from 'react'
import { kaufplan } from '../data/kaufplan'
import type { Urteil } from '../data/kaufplan.types'
import UrteilBadge from '../components/UrteilBadge'

type Filter = 'alle' | 'etf' | 'aktie'
const FILTERS: { id: Filter; label: string }[] = [
  { id: 'alle',  label: 'Alle' },
  { id: 'etf',   label: 'ETFs' },
  { id: 'aktie', label: 'Einzelaktien' },
]
const ORDER: Record<Urteil, number> = { kaufen: 0, staffeln: 1, warten: 2, meiden: 3 }

export default function Einstiege() {
  const [filter, setFilter] = useState<Filter>('alle')
  const rows = useMemo(
    () => kaufplan.einstiege
      .filter(e => filter === 'alle' || e.typ === filter)
      .sort((a, b) => ORDER[a.urteil] - ORDER[b.urteil] || (a.vomHoch ?? 0) - (b.vomHoch ?? 0)),
    [filter],
  )

  return (
    <main className="max-w-[1200px] mx-auto px-4 sm:px-5 py-8">
      <span className="font-mono text-[10px] font-bold tracking-[0.18em] text-accent">EINSTIEGE</span>
      <h1 className="text-[26px] font-bold text-t1 tracking-tight mt-1">Wo, zu welchem Preis, und wann die These kippt</h1>
      <p className="text-[14px] text-t2 mt-1.5 max-w-[760px]">
        Kaufzone = Bereich, in dem wir Tranchen setzen. Invalidierung = Punkt, an dem die These nicht mehr stimmt und man nicht nachkauft.
        Stand {new Date(kaufplan.tacheles.stand).toLocaleDateString('de-CH')}.
      </p>

      <div role="group" aria-label="Filter" className="flex gap-1.5 mt-6">
        {FILTERS.map(f => (
          <button key={f.id} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}
            className="font-mono text-[12px] px-3.5 py-2 rounded-lg min-h-[40px]"
            style={{
              color: filter === f.id ? 'var(--text-1)' : 'var(--text-3)',
              background: filter === f.id ? 'var(--surface-2)' : 'transparent',
              border: '1px solid var(--border)',
            }}>
            {f.label}
          </button>
        ))}
      </div>

      <p className="sr-only" aria-live="polite">{rows.length} Einträge</p>
      <div className="grid gap-3 mt-4 md:grid-cols-2">
        {rows.map(e => (
          <article key={e.name} className="card p-4 flex flex-col gap-2.5">
            <header className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-[15px] font-semibold text-t1 leading-snug">{e.name}</h2>
                <div className="font-mono text-[11px] text-t3">{e.ticker} · {e.bereich}</div>
              </div>
              <UrteilBadge u={e.urteil} />
            </header>
            <dl className="grid grid-cols-3 gap-2 font-mono text-[12px]">
              <div><dt className="text-[10px] text-t3">KURS</dt><dd className="text-t1 tabular-nums m-0">{e.kurs}</dd></div>
              <div><dt className="text-[10px] text-t3">VOM HOCH</dt>
                <dd className="tabular-nums m-0" style={{ color: (e.vomHoch ?? 0) <= -10 ? 'var(--neg)' : 'var(--text-1)' }}>{e.vomHoch === null ? 'n/a' : `${e.vomHoch.toFixed(1)}%`}</dd></div>
              <div><dt className="text-[10px] text-t3">BEWERTUNG</dt><dd className="text-t1 m-0">{e.kgv}</dd></div>
            </dl>
            <p className="text-[13px] text-t2 leading-relaxed">{e.warum}</p>
            <div className="grid grid-cols-2 gap-2 text-[12px] mt-auto pt-2.5" style={{ borderTop: '1px solid var(--border)' }}>
              <div><div className="font-mono text-[10px]" style={{ color: 'var(--pos)' }}>KAUFZONE</div><div className="text-t1">{e.kaufzone}</div></div>
              <div><div className="font-mono text-[10px]" style={{ color: 'var(--neg)' }}>THESE KIPPT</div><div className="text-t1">{e.invalidierung}</div></div>
            </div>
          </article>
        ))}
      </div>
    </main>
  )
}
